import { appConfig } from "@/config";
import { baseRefreshingQuery } from "@/core/api/baseQuery";
import { HttpMethod } from "@/types";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ConversionLogChunk,
  CreateModel,
  CreateModelConversion,
  DatasetStats,
  DatasetStatsResponse,
  Model,
  ModelConversion,
  ModelLog,
  ModelOutput,
  SuggestHyperparamsRequest,
  SuggestHyperparamsResponse,
  TrainingUsage,
} from "@repo/schema";

export enum ModelApiTagType {
  Model = "Models",
  /**
   * Lives here rather than on organizationApi: the two are separate createApi
   * slices with independent tag registries, so only mutations in this slice can
   * invalidate it — and the training mutations are what change it.
   */
  TrainingUsage = "TrainingUsage",
  Conversion = "Conversions",
}

const { projects, hyperparamSuggestion } = appConfig.studioApi.endpoints;
const modelApiBase = createApi({
  reducerPath: "modelApi",
  baseQuery: baseRefreshingQuery,
  tagTypes: Object.values(ModelApiTagType),
  endpoints: () => ({}),
});

export const modelApi = modelApiBase.injectEndpoints({
  endpoints: (builder) => ({
    getModels: builder.query<Model[], { projectId: number }>({
      query: ({ projectId }) => ({
        url: projects.models.models(projectId),
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: ModelApiTagType.Model, id: projectId },
      ],
    }),
    getModel: builder.query<Model, { projectId: number; modelId: number }>({
      query: ({ projectId, modelId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}`,
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: ModelApiTagType.Model, id: projectId },
      ],
    }),
    createModel: builder.mutation<Model, CreateModel & { projectId: number }>({
      query: ({ projectId, ...payload }) => ({
        url: projects.models.models(projectId),
        method: HttpMethod.POST,
        body: payload,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: ModelApiTagType.Model, id: projectId },
        ModelApiTagType.TrainingUsage,
      ],
    }),
    deleteModel: builder.mutation<void, { projectId: number; modelId: number }>(
      {
        query: ({ projectId, modelId }) => ({
          url: `${projects.models.models(projectId)}/${modelId}`,
          method: HttpMethod.DELETE,
        }),
        invalidatesTags: (_result, _error, { projectId }) => [
          { type: ModelApiTagType.Model, id: projectId },
        ],
      },
    ),
    trainModel: builder.mutation<void, { projectId: number; modelId: number }>({
      query: ({ projectId, modelId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/train`,
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: ModelApiTagType.Model, id: projectId },
        ModelApiTagType.TrainingUsage,
      ],
    }),
    cancelTraining: builder.mutation<
      Model,
      { projectId: number; modelId: number }
    >({
      query: ({ projectId, modelId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/cancel`,
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: ModelApiTagType.Model, id: projectId },
      ],
    }),
    getModelLogs: builder.query<
      ModelLog[],
      { projectId: number; modelId: number }
    >({
      query: ({ projectId, modelId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/logs`,
        method: HttpMethod.GET,
      }),
    }),
    getModelOutputs: builder.query<
      ModelOutput[],
      { projectId: number; modelId: number }
    >({
      query: ({ projectId, modelId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/outputs`,
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { modelId }) => [
        { type: ModelApiTagType.Conversion, id: modelId },
      ],
    }),
    /**
     * Conversions for a model. The GET is what drives reconciliation on the
     * API side, so polling it is also how a conversion whose job died without
     * reporting gets resolved.
     */
    getModelConversions: builder.query<
      ModelConversion[],
      { projectId: number; modelId: number }
    >({
      query: ({ projectId, modelId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/conversions`,
        method: HttpMethod.GET,
      }),
      providesTags: (_result, _error, { modelId }) => [
        { type: ModelApiTagType.Conversion, id: modelId },
      ],
    }),
    /**
     * One slice of a conversion's log, from byte `after` onwards.
     *
     * No `providesTags` and no `pollingInterval` — the dialog drives this with
     * a lazy trigger on an interval, because each `after` is a distinct cache
     * entry and declarative polling would re-fetch a fixed offset forever.
     */
    getConversionLog: builder.query<
      ConversionLogChunk,
      {
        projectId: number;
        modelId: number;
        conversionId: number;
        after: number;
      }
    >({
      query: ({ projectId, modelId, conversionId, after }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/conversions/${conversionId}/logs`,
        method: HttpMethod.GET,
        params: { after },
      }),
    }),
    /** Create, or restart when the same settings already exist. */
    createModelConversion: builder.mutation<
      ModelConversion,
      CreateModelConversion & { projectId: number; modelId: number }
    >({
      query: ({ projectId, modelId, ...payload }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/conversions`,
        method: HttpMethod.POST,
        body: payload,
      }),
      invalidatesTags: (_result, _error, { modelId }) => [
        { type: ModelApiTagType.Conversion, id: modelId },
      ],
    }),
    retryModelConversion: builder.mutation<
      ModelConversion,
      { projectId: number; modelId: number; conversionId: number }
    >({
      query: ({ projectId, modelId, conversionId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/conversions/${conversionId}/retry`,
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, _error, { modelId }) => [
        { type: ModelApiTagType.Conversion, id: modelId },
      ],
    }),
    cancelModelConversion: builder.mutation<
      void,
      { projectId: number; modelId: number; conversionId: number }
    >({
      query: ({ projectId, modelId, conversionId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/conversions/${conversionId}/cancel`,
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, _error, { modelId }) => [
        { type: ModelApiTagType.Conversion, id: modelId },
      ],
    }),
    /** Make this conversion's artifact the one deploy uses for its target. */
    activateModelConversion: builder.mutation<
      void,
      { projectId: number; modelId: number; conversionId: number }
    >({
      query: ({ projectId, modelId, conversionId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/conversions/${conversionId}/activate`,
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, _error, { modelId }) => [
        { type: ModelApiTagType.Conversion, id: modelId },
      ],
    }),
    deleteModelConversion: builder.mutation<
      void,
      { projectId: number; modelId: number; conversionId: number }
    >({
      query: ({ projectId, modelId, conversionId }) => ({
        url: `${projects.models.models(projectId)}/${modelId}/conversions/${conversionId}`,
        method: HttpMethod.DELETE,
      }),
      invalidatesTags: (_result, _error, { modelId }) => [
        { type: ModelApiTagType.Conversion, id: modelId },
      ],
    }),
    datasetStats: builder.mutation<
      DatasetStatsResponse,
      DatasetStats & { projectId: number }
    >({
      query: ({ projectId, ...payload }) => ({
        url: `${projects.models.models(projectId)}/dataset-stats`,
        method: HttpMethod.POST,
        body: payload,
      }),
    }),
    /** Trainings started this calendar month vs the org's maxTrainings flag. */
    getTrainingUsage: builder.query<TrainingUsage, void>({
      query: () => ({
        url: projects.trainingUsage,
        method: HttpMethod.GET,
      }),
      providesTags: [ModelApiTagType.TrainingUsage],
    }),
    suggestHyperparams: builder.mutation<
      SuggestHyperparamsResponse,
      SuggestHyperparamsRequest & { projectId: number }
    >({
      query: ({ projectId, ...payload }) => ({
        url: hyperparamSuggestion(projectId),
        method: HttpMethod.POST,
        body: payload,
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useCreateModelMutation,
  useTrainModelMutation,
  useCancelTrainingMutation,
  useGetModelsQuery,
  useGetModelQuery,
  useDeleteModelMutation,
  useGetModelLogsQuery,
  useGetModelOutputsQuery,
  useLazyGetModelOutputsQuery,
  useGetModelConversionsQuery,
  useLazyGetConversionLogQuery,
  useCreateModelConversionMutation,
  useRetryModelConversionMutation,
  useCancelModelConversionMutation,
  useActivateModelConversionMutation,
  useDeleteModelConversionMutation,
  useDatasetStatsMutation,
  useGetTrainingUsageQuery,
  useSuggestHyperparamsMutation,
} = modelApi;
